<script setup>
import IconSearch from '@/assets/icons/search.svg';
import IconCloseButton from '@/assets/icons/close-button.svg';

const props = defineProps({
  entities: {
    type: Array,
    default: () => [],
    readonly: true
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  isDesktopNeeded: {
    type: Boolean,
    default: false
  }
})

const { isMobileView } = useScreen()
const { entities, isLoading } = toRefs(props)

const emit = defineEmits(['input'])
const { isOpen, close, open } = useModal()

// Fallback for SSR
let isLocked = ref(false)

if (process.client) {
  // Only call useScrollLock in the browser
  isLocked = useScrollLock(document.body)
}

// Sync isOpen -> isLocked, but only if we're in the client
watch(isOpen, (value) => {
  if (process.client)
    isLocked.value = value
})

const searchInputRef = ref(null)
const searchInput = defineModel({ type: String })

onStartTyping(() => {
  if (searchInputRef.value && !searchInputRef.value.active) {
    searchInputRef.value.focus()
  }
})

const onInput = () => emit('input', searchInput.value)
const onClear = () => {
  searchInput.value = ''
  onInput()
}

defineExpose({
  open,
  close
})

const inputButtons = [
  { component: IconSearch, event: onInput, position: 'left' },
  { component: IconCloseButton, event: onClear, position: 'right' },
]
</script>

<template>
  <div v-if="!isMobileView && isDesktopNeeded" class="overflow-hidden overflow-y-hidden lg:w-[280px] lg:max-w-[280px] xl:w-[300px] xl:max-w-[300px] 2xl:w-[350px] 2xl:max-w-[350px]">
    <div class="relative w-full flex items-center">
      <input 
        ref="searchInputRef" 
        v-model="searchInput"
        class="w-full bg-gray-100 h-[48px] rounded-[10px] px-11 focus:outline-none"
        @input="onInput"
        @keydown.enter="onInput" 
      />
      <template v-for="(inputButton, index) in inputButtons" :key="index">
        <div class="absolute top-0 h-full flex items-center" :style="{ [inputButton.position]: 0 }">
          <button class="px-3 py-3.5" @click="inputButton.event">
            <component :is="inputButton.component" class="size-5" />
          </button>
        </div>
      </template>
    </div>
    <div v-if="isLoading || entities" class="border-t mt-4 rounded-full"></div>
    <div 
      v-if="isLoading || entities" 
      :class="['relative mt-4 max-h-[270px] overflow-x-hidden scrollbar scrollbar--round',
        { 'h-[280px]': isLoading },
        { 'overflow-hidden': isLoading || entities.length <= 4 },
        { 'overflow-y-auto': !isLoading && entities.length > 4 }
      ]"
    >
      <LoadingOverlay v-if="isLoading" class="max-h-[280px]" />
      <div class="text-darkBlue grid gap-3">
        <div v-if="(!entities || entities.length === 0) && !isLoading" class="flex justify-center">
          <p>{{ $t('no_results') }}</p>
        </div>
        <template v-else>
          <div v-for="entity in entities" :key="`${entity.entity_type}_${entity.id}`" class="lg:max-w-[280px] xl:max-w-[300px] 2xl:max-w-[350px]">
            <UseEntity :entity="entity" #="{ name, image, bio, rating, type, link }">
              <NuxtLink :to="link" class="group focus:outline-none">
                <div
                  class="flex items-center gap-3 truncate min-h-[56px] group-hover:text-lightBlue group-focus:text-lightBlue group-active:text-lightBlue transition-all">
                  <div class="w-full max-w-[50px] aspect-square rounded-full bg-darkBlue/10 overflow-hidden relative">
                    <GTImage :src="image" class="w-full h-full" loadingClass="z-[-120] p-0 m-0 [&_div]:-translate-y-[25%]">
                      <template #fallback>
                        <div v-if="type === 'lawyer'" class="overflow-hidden">
                          <img src="/images/lawyer.svg" alt="lawyer">
                        </div>
                        <div v-else-if="type === 'firm'" class="overflow-hidden">
                          <img src="/images/firm.svg" alt="Firm">
                        </div>
                      </template>
                    </GTImage>
                  </div>
                  <div class="col-span-5 truncate *:truncate flex flex-col justify-between h-full text-left">
                    <div class="font-medium">
                      {{ name }}
                    </div>
                    <div v-if="bio" class="text-xs">
                      {{ bio }}
                    </div>
                    <div>
                      <rating-stars :value="rating" :text-size="12" number-class="ml-[2px]" />
                    </div>
                  </div>
                </div>
              </NuxtLink>
            </UseEntity>
          </div>
        </template>
      </div>
    </div>
  </div>
  <Teleport v-if="isMobileView" to="body">
    <Transition name="fade">
      <div v-if="isOpen" class="fixed top-0 left-0 h-full w-full z-[12000] bg-white flex flex-col gap-3 font-normal">
        <div class="p-6 pb-0 flex gap-2.5">
          <button class="p-1" @click="close()">
            <IconArrowFullLeft class="size-6" />
          </button>

          <div class="relative w-full">
            <input 
              ref="searchInputRef"
              v-model="searchInput"
              class="w-full bg-gray-100 h-[48px] rounded-[10px] px-11 focus:outline-none"
              @input="onInput"
              @keydown.enter="onInput" 
            />

            <template v-for="(inputButton, index) in inputButtons" :key="index">
              <div class="absolute top-0 h-full flex items-center" :style="{ [inputButton.position]: 0 }">
                <button class="px-3 py-3.5" @click="inputButton.event">
                  <component :is="inputButton.component" class="size-5" />
                </button>
              </div>
            </template>
          </div>
        </div>

        <div class="flex-grow relative px-6 py-4 border-t overflow-y-scroll overflow-x-hidden scrollbar">
          <LoadingOverlay v-model="isLoading" />
          <p v-if="(!entities || entities.length === 0) && !isLoading" class="flex justify-center items-center absolute top-0 bottom-0 left-0 right-0">
            {{ $t('no_results') }}
          </p>
          <div class="text-darkBlue grid gap-3">
            <template v-for="entity in entities" :key="`${entity.entity_type}_${entity.id}`">
              <UseEntity :entity="entity" #="{ name, image, bio, rating, type, link }">
                <NuxtLink :to="link" class="group focus:outline-none">
                  <div class="flex items-center gap-3 truncate min-h-[56px] group-hover:text-lightBlue group-focus:text-lightBlue group-active:text-lightBlue transition-all">
                    <div class="w-full max-w-[50px] aspect-square rounded-full bg-darkBlue/10 max-h-[800px] overflow-hidden relative">
                      <GTImage :src="image" class="w-full h-full" loadingClass="z-[-120] p-0 m-0 [&_div]:-translate-y-[25%]">
                        <template #fallback>
                          <div v-if="type === 'lawyer'" class="overflow-hidden">
                            <img src="/images/lawyer.svg" alt="lawyer">
                          </div>
                          <div v-else-if="type === 'firm'">
                            <img src="/images/firm.svg" alt="Firm">
                          </div>
                        </template>
                      </GTImage>
                    </div>
                    <div class="col-span-5 truncate *:truncate flex flex-col justify-between h-full text-left">
                      <div class="font-medium">
                        {{ name }}
                      </div>
                      <div v-if="bio" class="text-xs">
                        {{ bio }}
                      </div>
                      <div>
                        <rating-stars :value="rating" :text-size="12" />
                      </div>
                    </div>
                  </div>
                </NuxtLink>
              </UseEntity>
            </template>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
